import {
  UserFlagsDetailsDTO,
  UsersWithFlagsDTO,
  UseSearchFlagsDTO,
} from '@shared/clientTypes/DTOs/FlagsDTO';
import { UserRole } from '@shared/enums';
import { ApiData, ApiDescription, CreateNewFlag, Flag, RequestsMethod } from '../clientTypes';

const BASE_URL = '/flags';

export const searchFlags: ApiDescription = {
  endpoint: `${BASE_URL}/search`,
  method: RequestsMethod.Get,
};

export type SearchFlags = ApiData<
  {
    page: number;
    limit: number;
    sort: 'asc' | 'desc';
    sortField?: 'date' | 'student' | 'mentor';
    search?: string;
    isActive?: string;
    flagType?: string;
    startRange?: string;
    endRange?: string;
    studentId?: string;
    mentorId?: string;
    courseId?: string;
    userStatus?: string;
  },
  UseSearchFlagsDTO
>;

export const exportFlagsCSV: ApiDescription = {
  endpoint: `${BASE_URL}/export-csv`,
  method: RequestsMethod.Get,
};

export type ExportFlagsCSV = ApiData<
  {
    sort?: 'asc' | 'desc';
    sortField?: 'date' | 'student' | 'mentor';
    search?: string;
    isActive?: string;
    flagType?: string;
    startRange?: string;
    endRange?: string;
    studentId?: string;
    mentorId?: string;
    courseId?: string;
    userStatus?: string;
  },
  string
>;

export const createFlag: ApiDescription = {
  endpoint: `${BASE_URL}`,
  method: RequestsMethod.Post,
};

export type CreateFlag = ApiData<CreateNewFlag, Flag>;

export const dismissFlag: ApiDescription = {
  endpoint: `${BASE_URL}/dismiss/:id`,
  method: RequestsMethod.Put,
};

export type DismissFlag = ApiData<{ closedBy: string }, Flag>;

export const deleteFlag: ApiDescription = {
  endpoint: `${BASE_URL}/:id`,
  method: RequestsMethod.Delete,
};

export type DeleteFlag = ApiData<never, unknown>;

export const getNoOfFlagsByFlagTypes: ApiDescription = {
  endpoint: `${BASE_URL}/get-no-of-flags-by-flagTypes`,
  method: RequestsMethod.Get,
};

export type GetNoOfFlagsByFlagTypes = ApiData<
  {
    search?: string;
    isActive?: string;
    flagType?: string;
    startRange?: string;
    endRange?: string;
    studentId?: string;
    mentorId?: string;
    courseId?: string;
  },
  any
>;

export const searchFlagsForLinearChart: ApiDescription = {
  endpoint: `${BASE_URL}/search-for-linear-chart`,
  method: RequestsMethod.Get,
};

export type SearchFlagsForLinearChart = ApiData<
  {
    search?: string;
    isActive?: string;
    flagType?: string;
    startRange?: string;
    endRange?: string;
    studentId?: string;
    mentorId?: string;
    courseId?: string;
  },
  any
>;

export const closeMultipleFlags: ApiDescription = {
  endpoint: `${BASE_URL}/close-multiple`,
  method: RequestsMethod.Put,
};

export type CloseMultipleFlags = ApiData<{ flagIds: string[]; closedBy: string }, unknown>;

export const getUsersWithFlags: ApiDescription = {
  endpoint: `${BASE_URL}/users-with-flags`,
  method: RequestsMethod.Get,
};

export type GetUsersWithFlags = ApiData<
  {
    page?: string;
    limit?: string;
    role: UserRole.Mentor | UserRole.Student;
    sortBy?: 'fullName' | 'activeFlags' | 'closedFlags' | 'totalFlags' | 'createdAt';
    sortOrder?: 'asc' | 'desc';
    search?: string;
    isActive?: string;
    flagType?: string;
    startRange?: string;
    endRange?: string;
    studentId?: string;
    mentorId?: string;
    courseId?: string;
    userStatus?: string;
  },
  UsersWithFlagsDTO
>;

export const getUserFlagsDetails: ApiDescription = {
  endpoint: `${BASE_URL}/user-flags-details`,
  method: RequestsMethod.Get,
};

export type GetUserFlagsDetails = ApiData<
  {
    userId: string;
    role: UserRole.Mentor | UserRole.Student;
  },
  UserFlagsDetailsDTO
>;

export const getMentorFlags: ApiDescription = {
  endpoint: `${BASE_URL}/mentor-flags`,
  method: RequestsMethod.Get,
};

export type GetMentorFlags = ApiData<
  {
    mentorId: string;
    page?: string;
    limit?: string;
    sortBy?: 'createdAt';
    sortOrder?: 'asc' | 'desc';
    isActive?: 'indeterminate' | 'checked' | 'unchecked';
  },
  UseSearchFlagsDTO
>;

export const getMentorFlagsNo: ApiDescription = {
  endpoint: `${BASE_URL}/mentor-flags-no`,
  method: RequestsMethod.Get,
};

export type GetMentorFlagsNo = ApiData<
  {
    mentorId: string;
  },
  { total: number }
>;

export const addMentorCommentToFlag: ApiDescription = {
  endpoint: `${BASE_URL}/add-mentor-comment/:id`,
  method: RequestsMethod.Put,
};

export type AddMentorCommentToFlag = ApiData<{ mentorComment: string }, Flag>;
